/*
 * DAC: finishing touches for the Rubik Variant Images swatches on the product page.
 *
 * Rubik renders its swatches inside a shadow root, so the theme's stylesheet cannot reach them. Sizes, gap and borders
 * are set from theme.css through Rubik's --rubik-swatch-* custom properties (those inherit into the shadow root). What
 * the variables cannot do is added here as a constructed stylesheet on each <rubik-swatch> shadow root.
 */
(() => {
  const CSS = `
    /* The tiles fill the full width of the swatch block (the same width as the buy buttons): an equal-column grid instead
       of fixed-size tiles. Column count and tile shape are set from theme.css (--dac-swatch-columns, --dac-swatch-ratio). */
    .rubik-swatch__items--visual {
      display: grid;
      grid-template-columns: repeat(var(--dac-swatch-columns, 5), minmax(0, 1fr));
      gap: var(--rubik-swatch-gap, 3px);
    }

    .rubik-swatch__items--visual .rubik-swatch__item-wrapper {
      display: block;
      min-width: 0;
    }

    .rubik-swatch__items--visual .rubik-swatch__label--image {
      display: block;
      width: 100%;
      height: auto;
      aspect-ratio: var(--dac-swatch-ratio, 3 / 2);
      box-sizing: border-box;
      padding: var(--dac-swatch-padding, 0);
    }

    .rubik-swatch__items--visual .rubik-swatch__caption {
      width: 100%;
    }

    /* Every swatch image sits on the same light grey tile; "multiply" lets the white photo background take that grey, so all tiles line up like a grid. */
    .rubik-swatch__label--image {
      background-color: var(--rubik-swatch-image-background-color, #fafafa);
    }

    .rubik-swatch__label--image .rubik-swatch__image {
      mix-blend-mode: multiply;
    }

    /* Sold-out colours: no diagonal strike, a white wash over the tile instead (strength: --dac-swatch-soldout-wash in theme.css) */
    .rubik-swatch__label--image .rubik-swatch__unavailable-overlay {
      display: none;
    }

    .rubik-swatch__label--image.rubik-swatch__label--unavailable::after {
      content: "";
      position: absolute;
      inset: 0;
      background: rgb(255 255 255 / var(--dac-swatch-soldout-wash, .65));
      pointer-events: none;
    }

    /* Option name: same look as the theme's own variant picker (grey label, strong value) */
    .rubik-swatch__option-name {
      font: var(--subheading-font-style, normal) var(--subheading-font-weight, 600) var(--subheading-font-size, 12px) / 1.6 var(--subheading-font-family, inherit);
      letter-spacing: var(--subheading-letter-spacing, normal);
      text-transform: var(--subheading-text-transform, uppercase);
      color: rgb(var(--text-color, 0 0 0));
    }

    .rubik-swatch__option-name-label {
      color: rgb(var(--text-color, 0 0 0) / .6);
      margin-inline-end: .5rem;
    }

    /* Hover tooltip (the full colour name): Rubik draws it on one line centred on the tile, so the long names on the right-hand
       tiles stuck out past the page edge (it made the page scroll sideways by ~180px). Wrap it to a fixed width instead, growing
       upwards from the tile, so it stays inside the page for every column. */
    .rubik-swatch__tooltip {
      width: max-content;
      max-width: 9rem;
      white-space: normal;
      text-align: center;
      top: auto;
      bottom: calc(100% + 8px);
    }
  `;

  let sheet = null;

  try {
    if ('adoptedStyleSheets' in Document.prototype && window.CSSStyleSheet) {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS);
    }
  } catch (error) {
    sheet = null;
  }

  // Rubik draws each colour's tile from the colour's own photo. For a product whose gallery opens on the front-on shot
  // (snippets/product-gallery.liquid swaps the first two photos) that is the angled shot of the first colour, so its tile
  // gets the front-on image too. The gallery lists the swap as JSON: { "<angled file name>": "<front-on image url>" }.
  const fileOf = (url) => {
    try {
      return decodeURIComponent(String(url).split('?')[0].split('/').pop()).toLowerCase();
    } catch (error) {
      return '';
    }
  };

  const frontImages = (root) => {
    const swaps = {};

    document.querySelectorAll('script[data-dac-swatch-front]').forEach((script) => {
      try {
        const pair = JSON.parse(script.textContent);

        Object.keys(pair).forEach((angled) => {
          swaps[angled.toLowerCase()] = pair[angled];
        });
      } catch (error) {
        // a malformed list just leaves the tiles as Rubik drew them
      }
    });

    if (!Object.keys(swaps).length) {
      return;
    }

    root.querySelectorAll('img.rubik-swatch__image').forEach((image) => {
      const front = swaps[fileOf(image.getAttribute('src') || '')];

      if (front) {
        image.removeAttribute('srcset');
        image.setAttribute('src', front);
      }
    });
  };

  const adopt = (element) => {
    const root = element.shadowRoot;

    if (!root) {
      return;
    }

    frontImages(root);

    if (sheet) {
      if (!root.adoptedStyleSheets.includes(sheet)) {
        root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      }

      return;
    }

    // Older browsers without constructed stylesheets: a <style> in the shadow root, re-added if Rubik wipes it
    if (!root.querySelector('style[data-dac-rubik]')) {
      const style = document.createElement('style');
      style.setAttribute('data-dac-rubik', '');
      style.textContent = CSS;
      root.appendChild(style);
    }
  };

  const adoptAll = () => document.querySelectorAll('rubik-swatch').forEach(adopt);

  adoptAll();

  // The element is created and upgraded after load (it can be late on slow connections), and the theme editor re-renders
  // sections, so react to every one of those moments instead of polling for a fixed time
  new MutationObserver(adoptAll).observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('load', adoptAll);
  document.addEventListener('shopify:section:load', adoptAll);

  if (window.customElements) {
    window.customElements.whenDefined('rubik-swatch').then(() => {
      adoptAll();
      window.setTimeout(adoptAll, 250);
    });
  }

  // Fallback for the (style fallback) case where Rubik rewrites the shadow content: cheap check twice a second
  window.setInterval(adoptAll, 500);
})();
